// src/BlogPostDetail.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CssBaseline, Container, Box, Typography, Button } from '@mui/joy';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BACKEND_API_URL } from '../data/constant';

function BlogPostDetail() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${BACKEND_API_URL}posts/${id}`,  {credentials: 'include'});
        if (!response.ok) {
          throw new Error('Failed to fetch blog post');
        }
        const data = await response.json();
        setPost(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography level="h4">Loading blog post...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography level="h4" color="danger">Error: {error}</Typography>
          <Button component={Link} to="/blog" variant="soft" color="primary" sx={{ mt: 2 }}>
            Back to Blog
          </Button>
        </Box>
      </Container>
    );
  }

  if (!post) {
    return (
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography level="h4">Post not found</Typography>
          <Button component={Link} to="/blog" variant="soft" color="primary" sx={{ mt: 2 }}>
            Back to Blog
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <CssBaseline />
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Typography level="h1" fontWeight="bold" component="h1" gutterBottom>
            {post.title}
          </Typography>
          <Typography level="body1" color="neutral" gutterBottom>
            By {post.author} on {post.date}
          </Typography>
          <Box sx={{ 
            '& h2': { mt: 4, mb: 2 },
            '& h3': { mt: 3, mb: 1.5 },
            '& p': { mb: 2 },
            '& ul, & ol': { pl: 2, mb: 2 },
            '& li': { mb: 1 },
            '& pre': { 
              backgroundColor: 'var(--joy-palette-background-level1)',
              p: 2,
              borderRadius: 'sm',
              overflowX: 'auto'
            },
            '& code': {
              fontFamily: 'monospace',
              backgroundColor: 'var(--joy-palette-background-level1)',
              px: 0.5,
              borderRadius: 'sm'
            },
            '& a': {
              color: 'var(--joy-palette-primary-solidBg)',
              textDecoration: 'underline'
            }
          }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {post.content}
            </ReactMarkdown>
          </Box>
          <Button component={Link} to="/blog" variant="soft" color="primary" sx={{ mt: 2 }}>
            Back to Blog
          </Button>
        </Box>
      </Container>
    </>
  );
}

export default BlogPostDetail;